<template>
  <section>
    <b-loading :active="isLoading" />
    <b-checkbox
      v-if="hasPermissionAdmMaster"
      v-model="applicabilitiesList.all_parts"
      :type="isContrast ? 'is-white' : 'is-primary'"
      class="has-text-color-7 mb-20"
      >{{ App.ModalEditorApplicability.APPLICABLE_TO_EVERY_PART }}</b-checkbox
    >

    <div v-if="!isMobile" class="columns is-mobile mb-0">
      <b-field
        :label="General.ORGAN_ENTITY"
        class="column is-4"
        customClass="has-text-color-8"
      />

      <b-field
        :label="fieldToSectorOrCells"
        class="column is-8"
        customClass="has-text-color-8"
      />
    </div>
    <div class="input-list">
      <div
        v-for="(part, index) in applicabilitiesList.parts"
        :key="index"
        class="is-mobile input-container"
      >
        <ValidationProvider
          v-slot="{ errors }"
          :rules="allPartsValue ? '' : 'required'"
        >
          <b-field
            :label="isMobile ? General.ORGAN_ENTITY : ''"
            class="input-part"
            :type="{ 'is-refused': errors.length }"
          >
            <b-autocomplete
              v-model="applicabilitiesList.parts[index].abbreviation"
              :field="isBookmark ? 'showName' : 'abbreviation'"
              :data="filteredParts(index)"
              :placeholder="App.ModalEditorApplicability.PART_PLACEHOLDER"
              autocomplete
              keepFirst
              :disabled="disabledPartField"
              @typing="setFilterTagTo"
              @select="option => setPartData(option, index)"
            >
              <template slot="empty">
                <p>
                  {{ General.MESSAGES.NO_ORGAN_ENTITY.toUpperCase() }}
                </p>
              </template>
            </b-autocomplete>
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>

        <div class="input-capacity">
          <ValidationProvider
            v-slot="{ errors }"
            :rules="isCapacityNotRequired(index) ? '' : 'required'"
          >
            <b-field
              :label="isMobile ? fieldToSectorOrCells : ''"
              :type="{ 'is-refused': errors.length }"
            >
              <CustomTagInput
                ref="tagCapacity"
                v-model="applicabilitiesList.parts[index].capacities"
                :field="isBookmark ? 'showName' : 'abbreviation'"
                :data="capacitiesList(part.id)"
                :beforeAdding="option => beforeAddingCapacity(option, index)"
                :placeholder="placeholderToSectorOrCells"
                autocomplete
                class="has-tag-ellipsis"
                :disabled="disabledCapacityField(index)"
                @typing="setFilterTagTo"
              >
                <template #selected="{ tags }">
                  <b-tag
                    v-for="(tag, indexTag) in tags"
                    :key="indexTag"
                    rounded
                    closable
                    @close="
                      $refs.tagCapacity[0].$refs.tagInput.removeTag(
                        indexTag,
                        $event
                      )
                    "
                  >
                    <b-tooltip
                      :label="tag.name || tag.description"
                      type="is-blue-oil"
                      size="is-large"
                      position="is-top"
                      multilined
                    >
                      <div class="tag-text is-flex">
                        <span class="has-ellipsis">
                          {{ tag.showName || tag.abbreviation }}
                        </span>
                      </div>
                    </b-tooltip>
                  </b-tag>
                </template>
                <template slot="empty">
                  <p>
                    {{ General.MESSAGES.NO_SECTOR_UNITY_FOUND.toUpperCase() }}
                  </p>
                </template>
              </CustomTagInput>
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>
          <b-checkbox
            v-model="applicabilitiesList.parts[index].all_capacities"
            :type="isContrast ? 'is-white' : 'is-primary'"
            class="has-text-color-7"
            :disabled="
              applicabilitiesList.all_parts ||
                !applicabilitiesList.parts[index].id
            "
            @input="forceUpdateComponent()"
            >{{ applicableToSectorOrCells }}</b-checkbox
          >
        </div>

        <div
          v-if="hasPermissionAdmMaster || applicabilitiesList.parts[index]"
          class="action-buttons action"
        >
          <b-icon
            v-if="
              hasPermissionAdmMaster && !applicabilitiesList.parts[index + 1]
            "
            :class="
              isLineCompleted(index)
                ? `is-clickable`
                : `custom-pointer-disabled`
            "
            :type="isLineCompleted(index) ? newApplicabilityColor : `is-grey`"
            icon="plus-circle-outline"
            @click.native="newApplicability(index)"
          />

          <b-icon
            v-if="showRemoveApplicability"
            class=" is-clickable"
            :type="isContrast ? 'is-white' : 'is-danger'"
            icon="minus-circle"
            @click.native="removeApplicability(index)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import { App, General } from "@/utils/constants";

export default {
  name: "ApplicabilityPartCapacity",
  props: {
    modelParts: {
      type: Object,
      default: () => {}
    },
    hasPermissionAdmMaster: {
      type: Boolean,
      default: false
    },
    hasPermissionAdmPart: {
      type: Boolean,
      default: false
    },
    hasPermissionAdmCapacity: {
      type: Boolean,
      default: false
    },
    isBookmark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      App,
      General,
      isLoading: false,
      filterTagTo: "",
      applicabilitiesList: {},
      initApplicabilityList: {
        all_parts: false,
        parts: [
          {
            id: null,
            all_capacities: false,
            name: "",
            abbreviation: "",
            capacities: []
          }
        ]
      }
    };
  },

  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
      getCurrentUser: "getCurrentUser",
      getFilteredParts: "getFilteredParts",
      getCapacitiesWithParamsFiltered: "getCapacitiesWithParamsFiltered",
      getPartsDistinct: "getPartsDistinct",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    disabledPartField() {
      return !this.hasPermissionAdmMaster || this.applicabilitiesList.all_parts;
    },

    applicableToSectorOrCells() {
      if (this.hasPermissionAdmCapacity)
        return App.ModalEditorApplicability.APPLICABLE_TO_EVERY_SECTOR;
      return App.ModalEditorApplicability.APPLICABLE_TO_EVERY_CAPACITY;
    },

    showRemoveApplicability() {
      return (
        this.applicabilitiesList.parts[0].id && this.hasPermissionAdmMaster
      );
    },

    fieldToSectorOrCells() {
      if (this.hasPermissionAdmCapacity)
        return App.ModalEditorApplicability.SECTOR;
      return General.CAPACITIES_UNITIES;
    },

    placeholderToSectorOrCells() {
      if (this.hasPermissionAdmCapacity)
        return App.ModalEditorApplicability.SECTOR_PLACEHOLDER;
      return App.ModalEditorApplicability.CAPACITY_PLACEHOLDER;
    },

    newApplicabilityColor() {
      return this.isContrast ? "is-white" : "is-primary";
    },

    allPartsValue() {
      return this.applicabilitiesList.all_parts;
    }
  },

  watch: {
    applicabilitiesList: {
      handler() {
        this.emitApplicability();
      },
      deep: true
    },
    modelParts: {
      handler() {
        this.applicabilitiesDataList();
      },
      deep: true
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.getCapacityRequest();
    this.applicabilitiesDataList();
    this.emitApplicability();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      partDistinctRequest: "partDistinctRequest",
      capacityWithParamsRequest: "capacityWithParamsRequest"
    }),

    isCapacityNotRequired(partIndex) {
      const allCapacities = this.applicabilitiesList.parts[partIndex]
        .all_capacities;
      return this.allPartsValue || allCapacities;
    },

    forceUpdateComponent() {
      this.$forceUpdate();
    },

    disabledCapacityField(index) {
      return (
        !this.applicabilitiesList.parts[index].id ||
        this.applicabilitiesList.all_parts ||
        this.applicabilitiesList.parts[index].all_capacities
      );
    },

    isLineCompleted(index) {
      return (
        this.applicabilitiesList.all_parts ||
        this.applicabilitiesList.parts[index]?.all_capacities ||
        this.applicabilitiesList.parts[index]?.capacities[index]?.id
      );
    },

    filteredParts() {
      return this.getFilteredParts(this.filterTagTo).filter(
        part =>
          !this.applicabilitiesList.parts.some(item => part.id === item.id)
      );
    },

    async getCapacityRequest() {
      await this.partDistinctRequest();
      if (this.hasPermissionAdmMaster) {
        await this.capacityWithParamsRequest();
      } else if (this.hasPermissionAdmPart) {
        await this.capacityWithParamsRequest({
          part_id: this.getCurrentUser.capacity.part_id
        });
      } else if (this.hasPermissionAdmCapacity) {
        await this.capacityWithParamsRequest({
          part_id: this.getCurrentUser.capacity.part_id,
          capacity_id: this.getCurrentUser.capacity.capacity_id
        });
      }
    },

    applicabilitiesDataList() {
      this.applicabilitiesList = cloneDeep(this.initApplicabilityList);

      if (this.modelParts?.parts?.length || this.modelParts?.all_parts) {
        this.applicabilitiesList = {
          ...this.modelParts,
          parts: this.modelParts.parts.length
            ? this.modelParts.parts
            : cloneDeep(this.initApplicabilityList.parts)
        };
      } else if (!this.hasPermissionAdmMaster) {
        this.applicabilitiesList.parts[0] = {
          ...this.applicabilitiesList.parts[0],
          ...this.getPartsDistinct.find(
            part => part.id === this.getCurrentUser.capacity.part_id
          )
        };
      }
    },

    setFilterTagTo(text) {
      this.filterTagTo = text;
    },

    async setPartData(option, key) {
      this.applicabilitiesList.parts[key].id = option?.id;
      this.applicabilitiesList.parts[key].name = option?.name;
      this.applicabilitiesList.parts[key].abbreviation = option?.abbreviation;
    },

    capacitiesList(partId) {
      return this.getCapacitiesWithParamsFiltered(this.filterTagTo)
        .filter(capacity => capacity.entityId == partId)
        .map(capacity => ({
          id: capacity.capacity_id,
          name: capacity.name,
          abbreviation: capacity.abbreviation,
          showName: `${
            capacity.abbreviation
          } - ${capacity.description.toUpperCase()}`
        }));
    },

    beforeAddingCapacity(option, index) {
      return !this.applicabilitiesList.parts[index].capacities.find(
        capacity => capacity.id === option.id,
        false
      );
    },

    newApplicability(index) {
      if (this.applicabilitiesList.parts[index].id) {
        this.applicabilitiesList.parts.push({
          ...this.initApplicabilityList.parts[0]
        });
      }
    },

    removeApplicability(key) {
      if (this.applicabilitiesList.parts.length === 1)
        this.applicabilitiesList = cloneDeep(this.initApplicabilityList);
      else this.applicabilitiesList.parts.splice(key, 1);
    },

    emitApplicability() {
      this.$emit("newApplicabilitiesList", {
        ...this.applicabilitiesList,
        parts: !this.applicabilitiesList.all_parts
          ? this.applicabilitiesList.parts.filter(part => part.id)
          : []
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.action-buttons {
  display: flex;
  gap: 0.75rem;
}

.input-part {
  grid-area: input-part;
}

.input-capacity {
  grid-area: input-capacity;
}

.action {
  margin-top: 6px;
  grid-area: action;
  @media screen and (max-width: $tablet) {
    align-self: center;
    margin-top: 12px;
  }
}

.input-container {
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
  grid-template-columns: 4fr 7fr 1fr;
  grid-template-rows: auto;
  grid-template-areas: "input-part input-capacity action";

  @media screen and (max-width: $tablet) {
    border-bottom: 1px solid $color-3;
    padding-bottom: 5px;
    grid-template-columns: 11fr 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      "input-part action"
      "input-capacity input-capacity";
  }
}
</style>
